import {useCallback, useEffect, useState} from 'react';

import type {ProcessorBalancesResponse} from '@/services/processorService';
import {processorService} from '@/services/processorService';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';
import {showErrorMessage} from '@/utils/showErrorMessage';

export default function useProcessorBalance() {
  const [balance, setBalance] = useState<ProcessorBalancesResponse | null>(
    null,
  );
  const [balanceDenomination, setBalanceDenomination] = useState<Record<
    string,
    | 'dollar-denominated-global'
    | 'local-denominated'
    | 'dollar-denominated-local'
  > | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProcessorBalanceDenomination = useCallback(async () => {
    try {
      const data = await processorService.getProcessorBalanceDenomination();
      setBalanceDenomination(data);
    } catch (e) {
      showErrorMessage(
        `Fetching processor balance denomination failed: ${getErrorMessage(e, String(e))}`,
      );
    }
  }, []);

  const fetchProcessorBalance = useCallback(async () => {
    try {
      const data = await processorService.getProcessorBalance();
      setBalance(data);
    } catch (e) {
      showErrorMessage(
        `Fetching processor balance failed: ${getErrorMessage(e, String(e))}`,
      );
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([
      fetchProcessorBalanceDenomination(),
      fetchProcessorBalance(),
    ]);
    setIsLoading(false);
  }, [fetchProcessorBalance, fetchProcessorBalanceDenomination]);

  useEffect(() => {
    // Both fetchers inside `fetchAll` try/catch into showErrorMessage, so this
    // only fires if that reporting itself throws. It goes to the dev logger
    // rather than a second toast: the admin has already been told about the
    // real failure, and if showErrorMessage is what threw, calling it again
    // would only throw again.
    fetchAll().catch(logger.error);
  }, [fetchAll]);

  return {
    balance,
    balanceDenomination,
    isLoading,
    refetch: fetchAll,
  };
}
